import { Box, Flex, Group, Pill, Stack, Text, Tooltip } from '@mantine/core';
import { IconProgressCheck } from '@tabler/icons-react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { Author } from '@feature/utils';

interface AuthorListProps {
  authors: Author[];
  affiliatedNo?: number | null;
  noOfRoAuthors?: number | null;
}

export function AuthorList(props: AuthorListProps) {
  const { authors, affiliatedNo, noOfRoAuthors } = props;
  const { t } = useTranslation();
  return (
    <Stack>
      <Text
        fw="500"
        size="xl"
      >
        {t('authors.form.title')}
      </Text>
      {affiliatedNo != null && (
        <Text
          size="sm"
          fw="500"
          c="dimmed"
        >
          {t('authors.list.universityAffiliated', { value: affiliatedNo })}
        </Text>
      )}
      {noOfRoAuthors != null && (
        <Text
          size="sm"
          fw="500"
          c="dimmed"
        >
          {t('authors.list.roAuthors', { value: noOfRoAuthors })}
        </Text>
      )}
      {authors.map((author, index) => (
        <Flex
          justify="space-between"
          align="center"
          bg="white"
          p="xs"
          gap="sm"
          style={{ border: '1px solid var(--mantine-color-content-border)', borderRadius: '12px' }}
          key={author.id}
        >
          <Box>
            <Group
              mb="1px"
              gap="xs"
            >
              <Text size="sm">#{index + 1}</Text>
              <Text
                size="sm"
                fw={500}
              >
                {author.userName}
              </Text>
              {author.membership?.name && <Pill bg="green">{author.membership?.name}</Pill>}
            </Group>
            <Group gap="3px">
              <Text
                size="xs"
                c="dimmed"
              >
                {author.alias}
              </Text>
              {author.reclaimed ? (
                <Tooltip
                  multiline
                  w={300}
                  label={t('authors.claimed.tooltip')}
                >
                  <IconProgressCheck
                    color="green"
                    size="14px"
                    stroke={2}
                  />
                </Tooltip>
              ) : (
                <Text
                  size="xs"
                  c="dimmed"
                  fs="italic"
                >
                  ({t('authors.unclaimed')})
                </Text>
              )}
            </Group>
          </Box>
          {(author.affiliationName || author.rorId) && (
            <Box>
              <Text
                size="xs"
                c="dimmed"
              >
                {t('authors.affiliation')}: {author.affiliationName}
              </Text>
              {author.rorId && (
                <Text
                  size="xs"
                  c="dimmed"
                >
                  (
                  <Link
                    to={author.rorId}
                    target="_blank"
                  >
                    {author.rorId}
                  </Link>
                  )
                </Text>
              )}
            </Box>
          )}
        </Flex>
      ))}
    </Stack>
  );
}
